import { scaleLinear, scalePoint } from '@visx/scale'
import { Group } from '@visx/group'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { LinePath } from '@visx/shape'
import { curveLinear, curveMonotoneX, curveStep } from '@visx/curve'
import type { ChartResult, ChartSeriesPoint, ChartSpec } from '../../../types/chart.js'
import { CHART_AXIS_COLOR, CHART_GRID_COLOR, getSeriesStyle } from './chartTheme.js'

export interface LineChartProps {
  spec: ChartSpec
  data: ChartResult
  width?: number
  height?: number
  className?: string
  testId?: string
  compact?: boolean
}

export function LineChart({
  spec,
  data,
  width = 500,
  height = 240,
  className = '',
  testId,
  compact = false,
}: LineChartProps) {
  const margin = compact
    ? { top: 2, right: 0, bottom: 2, left: 0 }
    : { top: 20, right: 20, bottom: 40, left: 50 }
  const xMax = Math.max(0, width - margin.left - margin.right)
  const yMax = Math.max(0, height - margin.top - margin.bottom)

  const xKeys = Array.from(
    new Set(data.series.flatMap((s) => s.points.map((p) => String(p.x)))),
  )

  const allY = data.series.flatMap((s) => s.points.map((p) => p.y))
  const maxY = Math.max(...allY, 1)

  const xScale = scalePoint<string>({
    domain: xKeys,
    range: [0, xMax],
    padding: 0.5,
  })

  const yScale = scaleLinear<number>({
    domain: [0, maxY],
    range: [yMax, 0],
    nice: true,
  })

  const curveType = spec.style?.curve === 'smooth'
    ? curveMonotoneX
    : (spec.style?.curve === 'step' ? curveStep : curveLinear)

  return (
    <svg
      width={width}
      height={height}
      className={`chartSvg ${className}`}
      data-testid={testId}
      role="img"
      aria-label={spec.title || 'Line Chart'}
    >
      <Group left={margin.left} top={margin.top}>
        {data.series.map((series, sIdx) => {
          const style = getSeriesStyle(sIdx, spec.style?.seriesColor?.[series.key])
          const strokeDash = style.strokeDasharray || (spec.style?.lineStyle === 'dashed' ? '4 4' : undefined)

          return (
            <Group key={series.key || sIdx}>
              <LinePath<ChartSeriesPoint>
                data={series.points}
                x={(p) => xScale(String(p.x)) || 0}
                y={(p) => yScale(p.y)}
                stroke={style.color}
                strokeWidth={compact ? 1.5 : 2}
                strokeDasharray={strokeDash}
                curve={curveType}
              />
              {series.points.map((p, pIdx) => (
                <circle
                  key={pIdx}
                  cx={xScale(String(p.x)) || 0}
                  cy={yScale(p.y)}
                  r={compact ? 2 : 3.5}
                  fill={style.color}
                />
              ))}
            </Group>
          )
        })}

        {!compact && (
          <>
            <AxisBottom
              top={yMax}
              scale={xScale}
              stroke={CHART_GRID_COLOR}
              tickStroke={CHART_GRID_COLOR}
              tickLabelProps={{
                fill: CHART_AXIS_COLOR,
                fontSize: 10,
                textAnchor: 'middle',
              }}
            />

            <AxisLeft
              scale={yScale}
              stroke={CHART_GRID_COLOR}
              tickStroke={CHART_GRID_COLOR}
              tickLabelProps={{
                fill: CHART_AXIS_COLOR,
                fontSize: 10,
                textAnchor: 'end',
                dx: -4,
              }}
            />
          </>
        )}
      </Group>
    </svg>
  )
}
